import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Select,
    MenuItem
} from '@mui/material';
import SuiButton from "../../components/SoftButton";
import { IoClose } from "react-icons/io5";
import { LuBuilding } from "react-icons/lu";
import { LiaUniversitySolid } from "react-icons/lia";
import { MdLocalPhone } from "react-icons/md";
import { HiMiniUsers } from "react-icons/hi2";
import { getUniversities } from "../../axios-client";
import "./assets/departmentsStyleSheet.css";
import PropTypes from "prop-types";
import { useSoftUIController } from "../../context";
import { LIBRARY_ADMIN_ROLE } from "../../utlis/constant";
import { useTranslation } from "react-i18next";

const AddDepartmentDialog = (props) => {
    const { t, i18n } = useTranslation('departments');
    const isRTL = i18n.dir() === 'rtl';
    const { open, onClose, handleAddDepartment } = props;
    const [isFocused, setIsFocused] = useState(false);
    const [controller] = useSoftUIController();
    const { role } = controller;

    const inputContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${isFocused ? '#cdcecf' : '#dcdfe3'}`,
        borderRadius: '8px',
        padding: '5px 10px',
        marginBottom: '10px',
        transition: 'border-color 0.3s ease'
    };

    const inputStyle = {
        width: '100%',
        padding: '10px 0px',
        border: 'none',
        fontSize: '14px',
        boxSizing: 'border-box',
        outline: 'none',
        fontFamily: 'Arial, Roboto'
    };

    const [universities, setUniversities] = useState([]);
    const [formData, setFormData] = useState({
        department: '',
        university: '',
        phone: '',
        maxUsers: 1
    });

    const [errors, setErrors] = useState({
        department: '',
        university: ''
    });

    useEffect(() => {
        getUniversities()
            .then(res => {
                setUniversities(res.data.universities);
                if (role === LIBRARY_ADMIN_ROLE && res.data.universities.length > 0 && !formData.university) {
                    setFormData(prev => ({
                        ...prev,
                        university: res.data.universities[0].id
                    }));
                }
            })
            .catch(error => {
                console.error('Error fetching universities:', error);
            });
    }, [role]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (e) => {
        setErrors(prevErrors => ({ ...prevErrors, university: '' }));
        setFormData(prev => ({ ...prev, university: e.target.value }));
    };

    const handleSave = () => {
        const newErrors = {};
        if (!formData.department) {
            newErrors.department = t("validation.department_required");
        }
        if (!formData.university) {
            newErrors.university = t("validation.university_required");
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        handleAddDepartment(formData);
    };

    return (
        <Dialog
            open={open}
            aria-labelledby="form-dialog-title"
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: { borderRadius: 15 } }}
        >
            <DialogTitle
                id="form-dialog-title"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: isRTL ? '1.3rem' : '1.5rem',
                    fontFamily: isRTL ? "'Tajawal', Arial, sans-serif" : "'Roboto', Arial, sans-serif",
                }}
            >
                {t("dialog.title")}
                <IoClose
                    aria-label="close"
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        [isRTL ? 'left' : 'right']: 16,
                        top: 16,
                        cursor: 'pointer'
                    }}
                />
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ padding: "0px 24px" }}>
                <label htmlFor="department" className="label-style">
                    {t("table_headers.department_name")}
                </label>
                <div style={inputContainerStyle}>
                    <LuBuilding className="icon-style" />
                    <input
                        type="text"
                        id="department"
                        name="department"
                        style={inputStyle}
                        value={formData.department}
                        onChange={handleInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                </div>
                {errors.department && <p style={{ color: 'red', fontSize: "12px" }}>{errors.department}</p>}

                <label htmlFor="university" className="label-style">{t("table_headers.university")}</label>
                <div style={inputContainerStyle}>
                    <LiaUniversitySolid className="icon-style" />
                    {role === LIBRARY_ADMIN_ROLE ? (
                        <input
                            type="text"
                            id="university"
                            name="university"
                            style={inputStyle}
                            value={
                                universities.find(u => u.id === formData.university)?.name || ''
                            }
                            disabled
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                        />
                    ) : (
                        <Select
                            id="university"
                            displayEmpty
                            fullWidth
                            value={formData.university}
                            onChange={handleSelectChange}
                            style={inputStyle}
                            inputProps={{ 'aria-label': 'Without label' }}
                        >
                            <MenuItem value="" disabled>{t("dialog.select_university")}</MenuItem>
                            {universities.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                    {u.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </div>
                {errors.university && <p style={{ color: 'red', fontSize: "12px" }}>{errors.university}</p>}

                <label htmlFor="phone" className="label-style">{t("table_headers.phone")}</label>
                <div style={inputContainerStyle}>
                    <MdLocalPhone className="icon-style" />
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        style={inputStyle}
                        value={formData.phone}
                        onChange={handleInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                </div>

                <label htmlFor="maxUsers" className="label-style">{t("table_headers.max_users")}</label>
                <div style={inputContainerStyle}>
                    <HiMiniUsers className="icon-style" />
                    <input
                        type="number"
                        id="maxUsers"
                        name="maxUsers"
                        style={inputStyle}
                        min={0}
                        value={formData.maxUsers}
                        onChange={handleInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                </div>
            </DialogContent>

            <Divider />

            <DialogActions>
                <SuiButton variant="gradient" color="secondary" size="medium" onClick={onClose}>
                    {t("button.cancel")}
                </SuiButton>
                <SuiButton variant="gradient" color="primary" size="medium" onClick={handleSave} sx={{ml: 1, mr:  (isRTL ? 1 : 0)}}>
                    {t("button.save")}
                </SuiButton>
            </DialogActions>
        </Dialog>
    );
};

AddDepartmentDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    handleAddDepartment: PropTypes.func
};

export default AddDepartmentDialog;
